// /create-template — DEV-ONLY: convert a legacy scene background into an AI
// template (Jun 13). A focused variant of /template-lab:
//
//   1. Pick one of the ~95 legacy scene backgrounds (public/templates/<id>/
//      frame1_empty.png) — or upload a custom reference — and drop in a MOCK
//      PRODUCT that suits it.
//   2. Generate: backend /api/dev/ai-template-lab renders the product into the
//      scene (OpenAI, selectable quality), then gpt-4.1 vision WRITES the
//      template prompt + name FROM the generated image.
//   3. Review + edit/re-render.
//   4. Save: the FULL-RES PNG becomes the backend runtime scene reference
//      (no quality loss — that PNG is what gpt-image sees at render time),
//      plus a small webp picker tile + the metadata/prompt overlays. Lands
//      live in /templates + the wizard immediately.
//
// Dev-only (import.meta.env.DEV); the save + backgrounds middleware only
// exist on the Vite dev server. No credits charged.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE, getAuthHeaders } from '@/lib/api';

const IS_DEV = import.meta.env.DEV;

type LabAspect = '1:1' | '9:16' | '4:5' | '16:9';
type LabQuality = 'low' | 'medium' | 'high';
type LabDomain = '' | 'skincare' | 'consumable' | 'wearable';

const ASPECTS: { id: LabAspect; label: string }[] = [
  { id: '1:1', label: '1:1 · 1080×1080' },
  { id: '9:16', label: '9:16 · 1080×1920' },
  { id: '4:5', label: '4:5 · 1440×1800' },
  { id: '16:9', label: '16:9 · 1920×1080' },
];

const QUALITIES: { id: LabQuality; label: string }[] = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
];

const CATEGORIES = ['studio', 'model', 'ingredient', 'contextual', 'lifestyle'] as const;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const resp = await fetch(dataUrl);
  return resp.blob();
}

/** Encode a data-URL image to a WebP (q0.9), PRESERVING its native aspect
 *  (long side ≤ 1024, no square crop) and return the base64 payload (no
 *  data: prefix). This is the picker tile only — the masonry template grids
 *  render it at its real shape, so a 9:16 / 4:5 template tile looks right.
 *  The runtime scene reference uses the full-res PNG (see the save handler). */
async function toSampleWebpBase64(dataUrl: string): Promise<string> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;
  });
  const MAX = 1024;
  const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((r) =>
    canvas.toBlob(r, 'image/webp', 0.9),
  );
  if (!blob) throw new Error('WebP encode failed');
  const buf = await blob.arrayBuffer();
  let bin = '';
  const arr = new Uint8Array(buf);
  for (let i = 0; i < arr.length; i += 0x8000) {
    bin += String.fromCharCode(...arr.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

export default function CreateTemplatePage() {
  // ---------- Inputs ----------
  const [productFile, setProductFile] = useState<File | null>(null);
  const [productPreview, setProductPreview] = useState<string | null>(null);
  const [refFile, setRefFile] = useState<File | null>(null);
  const [refPreview, setRefPreview] = useState<string | null>(null);
  const [aspect, setAspect] = useState<LabAspect>('1:1');
  // Medium by default for a fast iteration loop (~30-40s). High (~1-3 min)
  // is opt-in for the final render you save — the save preserves whatever
  // quality you generated at, losslessly (full-res PNG).
  const [quality, setQuality] = useState<LabQuality>('medium');
  const [pasteTarget, setPasteTarget] = useState<'reference' | 'product'>('product');

  // ---------- Background library ----------
  const [backgrounds, setBackgrounds] = useState<string[]>([]);
  const [loadingBgs, setLoadingBgs] = useState(true);
  const [selectedBg, setSelectedBg] = useState<string | null>(null);

  // ---------- Generation ----------
  const [editInstruction, setEditInstruction] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState<string | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null); // data URL
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ---------- Save ----------
  const [saveName, setSaveName] = useState('');
  const [saveRoute, setSaveRoute] = useState<'product-shot' | 'product-swap'>('product-shot');
  const [saveCategory, setSaveCategory] = useState<typeof CATEGORIES[number]>('studio');
  const [saveDomain, setSaveDomain] = useState<LabDomain>('');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const saveId = useMemo(() => slugify(saveName), [saveName]);

  const setProduct = useCallback(async (file: File) => {
    setProductFile(file);
    setProductPreview(await fileToDataUrl(file));
  }, []);

  const setReference = useCallback(async (file: File) => {
    setRefFile(file);
    setRefPreview(await fileToDataUrl(file));
  }, []);

  // ---------- Load the legacy scene backgrounds (dev middleware) ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/dev-list-backgrounds');
        const data = await res.json();
        if (!cancelled) setBackgrounds(Array.isArray(data.backgrounds) ? data.backgrounds : []);
      } catch {
        if (!cancelled) setBackgrounds([]);
      } finally {
        if (!cancelled) setLoadingBgs(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pick a legacy background → load its frame1_empty.png as the reference.
  const selectBackground = useCallback(async (bg: string) => {
    try {
      setError(null);
      const resp = await fetch(`/templates/${bg}/frame1_empty.png`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const file = new File([blob], `${bg}-bg.png`, { type: blob.type || 'image/png' });
      setSelectedBg(bg);
      await setReference(file);
    } catch (err) {
      setError(err instanceof Error ? `Couldn't load ${bg}: ${err.message}` : 'Failed to load background.');
    }
  }, [setReference]);

  // Page-level paste — routes a clipboard image to the active paste target.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (!item) return;
      const file = item.getAsFile();
      if (!file) return;
      e.preventDefault();
      if (pasteTarget === 'product') void setProduct(file);
      else { setSelectedBg(null); void setReference(file); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [pasteTarget, setProduct, setReference]);

  // ---------- Generate / Re-render ----------
  const runGenerate = useCallback(async (promptOverride?: string) => {
    if (!productFile || generating) return;
    if (!promptOverride && !refFile) return;
    setGenerating(true);
    setError(null);
    setSavedId(null);
    const slowHint = quality === 'high' ? ' — high quality can take 1–3 min' : '';
    setGenStatus(promptOverride ? `Rendering with your edited prompt${slowHint}…` : `Placing your product into the background${slowHint}…`);
    try {
      const fd = new FormData();
      fd.append('product', productFile);
      if (refFile) fd.append('reference', refFile);
      fd.append('aspect_ratio', aspect);
      fd.append('quality', quality);
      if (promptOverride) fd.append('prompt_override', promptOverride);
      if (!promptOverride) {
        // Heuristic flip — keep "rendering" showing longer at high quality so
        // the status doesn't claim "writing prompt" while the render is still
        // in flight (it's a timer, not real progress).
        const flipMs = quality === 'high' ? 110000 : 40000;
        setTimeout(() => setGenStatus((s) => (s ? 'Writing the template prompt from the result…' : s)), flipMs);
      }
      const res = await fetch(`${API_BASE}/api/dev/ai-template-lab`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResultImage(`data:image/png;base64,${data.image_b64}`);
      setPrompt(data.prompt);
      if (!promptOverride && data.name_suggestion) {
        setSaveName(data.name_suggestion);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed.');
    } finally {
      setGenerating(false);
      setGenStatus(null);
    }
  }, [productFile, refFile, aspect, quality, generating]);

  // ---------- Edit the generated image ----------
  const runEdit = useCallback(async () => {
    const instruction = editInstruction.trim();
    if (!productFile || !resultImage || !instruction || generating) return;
    setGenerating(true);
    setError(null);
    setSavedId(null);
    setGenStatus(`Applying your edit${quality === 'high' ? ' — high quality can take 1–3 min' : ''}…`);
    setTimeout(() => setGenStatus((s) => (s ? 'Writing the updated prompt…' : s)), quality === 'high' ? 110000 : 40000);
    try {
      const fd = new FormData();
      fd.append('product', productFile);
      fd.append('aspect_ratio', aspect);
      fd.append('quality', quality);
      fd.append('edit_instruction', instruction);
      fd.append('base_image', await dataUrlToBlob(resultImage), 'base.png');
      const res = await fetch(`${API_BASE}/api/dev/ai-template-lab`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResultImage(`data:image/png;base64,${data.image_b64}`);
      setPrompt(data.prompt);
      setEditInstruction('');
      if (!saveName && data.name_suggestion) setSaveName(data.name_suggestion);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Edit failed.');
    } finally {
      setGenerating(false);
      setGenStatus(null);
    }
  }, [editInstruction, productFile, resultImage, aspect, quality, generating, saveName]);

  // ---------- Restore last run ----------
  const [restoring, setRestoring] = useState(false);
  const handleRestoreLast = useCallback(async () => {
    if (restoring || generating) return;
    setRestoring(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/dev/ai-template-lab/last`, {
        headers: await getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `No persisted run found (HTTP ${res.status})`);
      }
      const data = await res.json();
      setResultImage(`data:image/png;base64,${data.image_b64}`);
      setPrompt(data.prompt ?? '');
      if (data.aspect_ratio) setAspect(data.aspect_ratio as LabAspect);
      if (!saveName && data.name_suggestion) setSaveName(data.name_suggestion);
      setSavedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setRestoring(false);
    }
  }, [restoring, generating, saveName]);

  // ---------- Save / Cancel ----------
  const handleSave = useCallback(async () => {
    if (!resultImage || !prompt.trim() || !saveId || saving) return;
    setSaving(true);
    setError(null);
    try {
      const sampleWebpBase64 = await toSampleWebpBase64(resultImage);
      // Full-res PNG — becomes the backend runtime scene reference (no
      // quality loss; the middleware writes it to backend/ai-templates/
      // product-shot/<id>.png and wizard.py reads PNG-first).
      const sourcePngBase64 = resultImage.split(',')[1];
      const res = await fetch('/api/dev-ai-template-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: saveId,
          name: saveName.trim(),
          category: saveCategory,
          route: saveRoute,
          domain: saveDomain || undefined,
          prompt: prompt.trim(),
          sampleWebpBase64,
          sourcePngBase64,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setSavedId(saveId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [resultImage, prompt, saveId, saveName, saveCategory, saveRoute, saveDomain, saving]);

  const handleCancel = useCallback(() => {
    setResultImage(null);
    setPrompt('');
    setSaveName('');
    setSaveDomain('');
    setSavedId(null);
    setError(null);
  }, []);

  if (!IS_DEV) {
    return (
      <div className="min-h-screen flex items-center justify-center text-dark-muted text-sm">
        Create Template is a dev-only tool — run it on the local dev server.
      </div>
    );
  }

  const canGenerate = !!productFile && !!refFile && !generating;

  return (
    <div className="min-h-screen bg-surface px-6 py-8">
      <div className="max-w-[1400px] mx-auto">

        {/* ---- Header ---- */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-dark text-2xl flex items-center gap-2">
              <i className="ri-add-box-line text-primary" />
              Create Template
              <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-wide">
                dev
              </span>
            </h1>
            <p className="text-dark-muted text-sm mt-1 max-w-2xl">
              Pick a scene background, drop in a mock product that suits it — the
              product gets placed into the scene, then OpenAI writes the template
              prompt FROM the result. Save publishes it (full-res PNG as the
              runtime reference) straight into the template library.
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <button
              type="button"
              onClick={() => void handleRestoreLast()}
              disabled={restoring || generating}
              title="Every generation is persisted server-side — recover the most recent one after an accidental page reload"
              className="inline-flex items-center gap-1.5 text-sm text-dark-muted hover:text-primary transition-colors cursor-pointer disabled:opacity-50"
            >
              <i className={restoring ? 'ri-loader-4-line animate-spin' : 'ri-history-line'} />
              Restore last run
            </button>
            <a href="/templates" className="text-sm text-primary hover:underline">
              Open /templates →
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* ============ LEFT: inputs ============ */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Product zone */}
              <InputZone
                title="1 · Mock product"
                hint="Click to upload — or set as paste target and ⌘V"
                preview={productPreview}
                isPasteTarget={pasteTarget === 'product'}
                onActivate={() => setPasteTarget('product')}
                onFile={(f) => void setProduct(f)}
              />
              {/* Reference / chosen background */}
              <InputZone
                title="2 · Background (reference)"
                hint="Pick one below — or click here + ⌘V / upload a custom one"
                preview={refPreview}
                isPasteTarget={pasteTarget === 'reference'}
                onActivate={() => setPasteTarget('reference')}
                onFile={(f) => { setSelectedBg(null); void setReference(f); }}
              />
            </div>

            {/* Background picker */}
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wide text-dark flex items-center gap-2">
                  <i className="ri-gallery-line text-primary" />
                  Pick a background
                </p>
                <span className="text-[11px] text-dark-muted">
                  {backgrounds.length} scenes · or upload a custom one above
                </span>
              </div>
              {loadingBgs ? (
                <p className="text-xs text-dark-muted py-8 text-center">Loading backgrounds…</p>
              ) : backgrounds.length === 0 ? (
                <p className="text-xs text-dark-muted py-8 text-center">
                  No backgrounds found (dev server only).
                </p>
              ) : (
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto pr-1">
                  {backgrounds.map((bg) => (
                    <button
                      key={bg}
                      type="button"
                      onClick={() => void selectBackground(bg)}
                      title={bg}
                      className={`
                        relative aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-pointer
                        ${selectedBg === bg
                          ? 'border-primary ring-2 ring-primary/30'
                          : 'border-surface-dark/30 hover:border-primary/50'}
                      `}
                    >
                      <img
                        src={`/templates/${bg}/frame1_thumb.png`}
                        alt={bg}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        draggable={false}
                      />
                      {selectedBg === bg && (
                        <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center shadow">
                          <i className="ri-check-line text-[10px]" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Aspect + quality */}
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">Aspect</span>
                  <div className="flex p-0.5 rounded-full bg-surface border border-surface-dark/30">
                    {ASPECTS.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setAspect(a.id)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${aspect === a.id ? 'bg-white text-primary shadow' : 'text-dark-muted'}`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">Quality</span>
                  <div className="flex p-0.5 rounded-full bg-surface border border-surface-dark/30">
                    {QUALITIES.map((q) => (
                      <button
                        key={q.id}
                        type="button"
                        onClick={() => setQuality(q.id)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer capitalize ${quality === q.id ? 'bg-white text-primary shadow' : 'text-dark-muted'}`}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-dark-muted">
                These templates are the scene authority for every future render —
                <strong className="text-dark"> High</strong> recommended (~$0.19/render vs ~$0.07 medium).
              </p>
            </div>

            <button
              type="button"
              onClick={() => runGenerate()}
              disabled={!canGenerate}
              className={`
                w-full inline-flex items-center justify-center gap-2
                px-6 py-3.5 rounded-full text-sm font-bold transition-all cursor-pointer
                ${canGenerate
                  ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                  : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
              `}
            >
              {generating ? (
                <><i className="ri-loader-4-line animate-spin" /> {genStatus ?? 'Generating…'}</>
              ) : (
                <><i className="ri-sparkling-2-line" /> Generate template</>
              )}
            </button>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Prompt (after generation) — editable + re-render */}
            {prompt && (
              <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-dark flex items-center gap-2">
                    <i className="ri-file-text-line text-base text-primary" />
                    Template prompt (editable)
                  </p>
                  <span className="text-[11px] text-dark-muted">{prompt.length} chars</span>
                </div>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={14}
                  className="w-full px-3 py-2.5 rounded-xl bg-surface/60 border border-dark-muted/30 text-xs leading-relaxed text-dark font-mono focus:outline-none focus:border-primary"
                />
                <button
                  type="button"
                  onClick={() => runGenerate(prompt)}
                  disabled={generating || !productFile || !prompt.trim()}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-primary/40 bg-white text-primary text-xs font-bold hover:bg-primary-light transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-refresh-line" />
                  Re-render with edited prompt
                </button>
              </div>
            )}
          </div>

          {/* ============ RIGHT: result + save ============ */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 overflow-hidden">
              <div className="bg-dark/95 flex items-center justify-center p-4 min-h-[320px]">
                {resultImage ? (
                  <img
                    src={resultImage}
                    alt="Generated template sample"
                    className="max-w-full max-h-[560px] rounded-lg"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-white/40 py-16">
                    {generating ? (
                      <>
                        <i className="ri-loader-4-line text-3xl animate-spin" />
                        <span className="text-xs font-semibold">{genStatus}</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-image-2-line text-4xl" />
                        <span className="text-xs font-semibold">
                          Generated sample previews here
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Edit box — surgical changes to the current result. */}
            {resultImage && (
              <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-dark flex items-center gap-2 mb-2">
                  <i className="ri-eraser-line text-base text-primary" />
                  Edit the image
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editInstruction}
                    onChange={(e) => setEditInstruction(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void runEdit(); }}
                    placeholder='e.g. "remove the second bottle" or "soften the shadows"'
                    maxLength={200}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-dark-muted/40 text-sm text-dark placeholder:text-dark-muted/60 focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => void runEdit()}
                    disabled={!editInstruction.trim() || generating || saving}
                    className={`
                      flex-shrink-0 inline-flex items-center gap-1.5
                      px-4 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer
                      ${editInstruction.trim() && !generating && !saving
                        ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                        : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
                    `}
                  >
                    <i className="ri-magic-line" />
                    Apply
                  </button>
                </div>
                <p className="text-[11px] text-dark-muted mt-2">
                  Changes only what you ask — everything else stays put. The
                  template prompt is re-written from the edited result.
                </p>
              </div>
            )}

            {/* Save panel */}
            {resultImage && (
              <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-dark flex items-center gap-2 mb-3">
                  <i className="ri-save-line text-base text-primary" />
                  Save as template
                </p>
                {savedId ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    <p className="font-bold flex items-center gap-1.5">
                      <i className="ri-check-line" />
                      Saved as <code className="px-1.5 py-0.5 rounded bg-emerald-100">{savedId}</code>
                    </p>
                    <p className="text-xs mt-1.5">
                      Live now in <a href="/templates" className="underline font-semibold">/templates</a> and
                      the wizard. The full-res PNG is the runtime scene reference (no
                      quality loss). Commit
                      <code className="mx-1 px-1 py-0.5 rounded bg-emerald-100 text-[11px]">ai-templates-custom.json</code>
                      (both sides) + the sample assets to ship it to prod.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        placeholder="Template name — e.g. Marble Dawn"
                        maxLength={24}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-dark-muted/40 text-sm text-dark placeholder:text-dark-muted/60 focus:outline-none focus:border-primary"
                      />
                      <span className="text-[11px] text-dark-muted font-mono flex-shrink-0">
                        id: {saveId || '—'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                      <label className="flex items-center gap-2 text-xs font-bold text-dark-muted uppercase tracking-wide">
                        Route
                        <select
                          value={saveRoute}
                          onChange={(e) => setSaveRoute(e.target.value as 'product-shot' | 'product-swap')}
                          className="px-3 py-1.5 rounded-lg border border-dark-muted/30 bg-white text-sm text-dark normal-case font-medium"
                        >
                          <option value="product-shot">Product Shot</option>
                          <option value="product-swap">In Hand</option>
                        </select>
                      </label>
                      <label className="flex items-center gap-2 text-xs font-bold text-dark-muted uppercase tracking-wide">
                        Category
                        <select
                          value={saveCategory}
                          onChange={(e) => setSaveCategory(e.target.value as typeof CATEGORIES[number])}
                          className="px-3 py-1.5 rounded-lg border border-dark-muted/30 bg-white text-sm text-dark normal-case font-medium"
                        >
                          {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </label>
                      <label className="flex items-center gap-2 text-xs font-bold text-dark-muted uppercase tracking-wide">
                        Domain
                        <select
                          value={saveDomain}
                          onChange={(e) => setSaveDomain(e.target.value as LabDomain)}
                          className="px-3 py-1.5 rounded-lg border border-dark-muted/30 bg-white text-sm text-dark normal-case font-medium"
                          title="Drives the 'Recommended for <domain>' grouping on the Templates tab"
                        >
                          <option value="">none</option>
                          <option value="skincare">skincare</option>
                          <option value="consumable">consumable</option>
                          <option value="wearable">wearable</option>
                        </select>
                      </label>
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={!saveId || saving}
                        className={`
                          flex-1 inline-flex items-center justify-center gap-2
                          px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer
                          ${saveId && !saving
                            ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                            : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
                        `}
                      >
                        {saving ? (
                          <><i className="ri-loader-4-line animate-spin" /> Saving…</>
                        ) : (
                          <><i className="ri-save-line" /> Save to library</>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-6 py-3 rounded-full border border-surface-dark/40 bg-white text-dark text-sm font-bold hover:bg-surface transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// InputZone — image input with file picker + paste-target activation.
// =============================================================================

function InputZone({ title, hint, preview, isPasteTarget, onActivate, onFile }: {
  title: string;
  hint: string;
  preview: string | null;
  isPasteTarget: boolean;
  onActivate: () => void;
  onFile: (f: File) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onFocus={onActivate}
      className={`
        bg-white rounded-2xl border-2 p-3 cursor-pointer transition-all
        ${isPasteTarget ? 'border-primary ring-2 ring-primary/25' : 'border-surface-dark/30 hover:border-primary/40'}
      `}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-dark">{title}</p>
        {isPasteTarget && (
          <span className="text-[10px] font-bold text-primary uppercase tracking-wide">
            ⌘V target
          </span>
        )}
      </div>
      <div className="relative aspect-square rounded-xl bg-surface overflow-hidden flex items-center justify-center">
        {preview ? (
          <img src={preview} alt="" className="absolute inset-0 w-full h-full object-contain" />
        ) : (
          <p className="text-[11px] text-dark-muted text-center px-4">{hint}</p>
        )}
      </div>
      <label
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-primary hover:underline cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <i className="ri-upload-2-line" />
        {preview ? 'Replace from file' : 'Upload from file'}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = '';
          }}
        />
      </label>
    </div>
  );
}
